import { RefObject } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useHistory } from 'react-router-dom';
import { isArray } from 'lodash';
import { patchTenantGroup } from 'Services';
import { FormikProps } from 'formik';
import { RQ_TENANT_GROUPS, RQ_UPDATE_TENANT_GROUP, RQ_GET_TENANT_GROUP } from 'Utils';
import useAlertBanner from 'Hooks/reduxHelperHooks/useAlertBanner';
import useReduxModal from 'Hooks/reduxHelperHooks/useReduxModal';

const useUpdateTenantGroup = (
    tenantGroupId: string,
    formRef: RefObject<FormikProps<PatchTenantGroupPayload>>,
    setCloseModal: (state: boolean) => void
) => {
    const { t } = useTranslation();
    const history = useHistory();
    const { addAlert } = useAlertBanner();
    const { clearAll, addTechnicalErrorModal } = useReduxModal();
    const queryClient = useQueryClient();

    const handleOnSuccess = (variable: PatchTenantGroupPayload) => {
        addAlert({
            id: 'update-tenant-group-success',
            content: t('update Tenant Group success message', {
                newTenantGroupName: variable.name
            }),
            status: 'success'
        });
        queryClient.invalidateQueries([RQ_UPDATE_TENANT_GROUP]);
        queryClient.invalidateQueries([RQ_TENANT_GROUPS]);
        queryClient.invalidateQueries([RQ_GET_TENANT_GROUP, tenantGroupId]);
        setCloseModal(true);
    };

    const handleOnError = (errRes: PatchTenantGroupErrorResponse, variable: PatchTenantGroupPayload) => {
        if (!isArray(errRes.data) && errRes.data.statusCode === 404) {
            addTechnicalErrorModal(
                '',
                t('Tenant Group Unavailable'),
                () => {
                    queryClient.invalidateQueries([RQ_TENANT_GROUPS]);
                    clearAll();
                    history.push('/tenants');
                },
                {
                    content: t('tenantGroupDeletedMsg', { tenantGroupName: variable.name }),
                    status: 'error',
                    cancelText: t('Return to Manage Tenants')
                }
            );
            queryClient.invalidateQueries([RQ_TENANT_GROUPS]);
        }

        // multiple errors
        if (isArray(errRes.data)) {
            errRes.data.forEach((o) => {
                const fieldName = o.error.msg.param;
                const errMsg = o.error.msg.error;
                formRef.current?.setFieldError(fieldName, errMsg);
            });
        } else {
            // single error
            const fieldName = errRes.data.error.msg.param;
            const errMsg = errRes.data.error.msg.error;
            formRef.current?.setFieldError(fieldName, errMsg);
        }
    };

    return useMutation(
        [RQ_UPDATE_TENANT_GROUP],
        (payload: PatchTenantGroupPayload) => patchTenantGroup(tenantGroupId, payload),
        {
            onSuccess: (_data, variable) => {
                handleOnSuccess(variable);
            },
            onError: (data: PatchTenantGroupErrorResponse, variable) => {
                handleOnError(data, variable);
            }
        }
    );
};

export default useUpdateTenantGroup;
